import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SamplingDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, in_progress: 0, approved: 0, rejected: 0 });
  const [samples, setSamples] = useState([]);

  useEffect(() => {
    fetchSamples();
  }, []);

  const fetchSamples = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/samples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamples(response.data);
      setStats({
        total: response.data.length,
        in_progress: response.data.filter(s => s.status === 'in_progress').length,
        approved: response.data.filter(s => s.status === 'approved').length,
        rejected: response.data.filter(s => s.status === 'rejected').length
      });
    } catch (error) {
      console.error('Failed to fetch samples');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Sampling Dashboard</h1>
              <p className="text-sm text-gray-600">Overview of all samples</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="total-samples-card">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Total Samples</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card data-testid="in-progress-card">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{stats.in_progress}</p>
            </CardContent>
          </Card>
          <Card data-testid="approved-card">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Approved</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
            </CardContent>
          </Card>
          <Card data-testid="rejected-card">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Rejected</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Samples</h2>
          <Button onClick={() => navigate('/sample-tracker')} data-testid="view-all-samples">View All Samples</Button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {samples.slice(0, 10).map((sample) => (
                <tr key={sample.id} data-testid={`sample-row-${sample.id}`}>
                  <td className="px-6 py-4">{sample.sample_number}</td>
                  <td className="px-6 py-4">{sample.article_name}</td>
                  <td className="px-6 py-4">{sample.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      sample.status === 'approved' ? 'bg-green-100 text-green-800' :
                      sample.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>{sample.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
